import { useState } from 'react';
import { Card, useToast } from '../ui';
import { Segmented } from '../ui/Segmented';
import { CrudScreen } from './CrudScreen';
import { OPERADORES } from './recursos';
import { api, ApiError } from '../lib/api';

type Disponibilidade = 'AVAILABLE' | 'BUSY' | 'OFFLINE';

const OPCOES = [
  { valor: 'AVAILABLE' as const, rotulo: 'Disponível' },
  { valor: 'BUSY' as const, rotulo: 'Ocupado' },
  { valor: 'OFFLINE' as const, rotulo: 'Fora' },
];

const EFEITO: Record<Disponibilidade, string> = {
  AVAILABLE: 'Você entra na distribuição automática e pode receber conversas novas.',
  BUSY: 'Você mantém as conversas atuais, mas não recebe novas pela distribuição.',
  OFFLINE: 'Você sai da distribuição. Conversas já atribuídas continuam com você.',
};

/**
 * Operadores + o controle de disponibilidade do próprio usuário.
 *
 * O controle existia no front antigo e se perdeu na migração — é ele que
 * decide se você recebe conversas novas, então perdê-lo não é detalhe: um
 * atendente sem como se marcar como ocupado continua recebendo fila enquanto
 * almoça.
 *
 * Fica ACIMA da tabela de propósito. É a única coisa nesta tela que afeta você
 * diretamente; a tabela abaixo é sobre os outros.
 */
function MinhaDisponibilidade() {
  const toast = useToast();
  // A sessão não devolve a disponibilidade atual, então o estado começa no
  // otimista e só muda por ação explícita. Assumir "Disponível" aqui é honesto:
  // é o padrão do banco para agente ativo.
  const [valor, setValor] = useState<Disponibilidade>('AVAILABLE');
  const [salvando, setSalvando] = useState(false);

  async function alterar(novo: Disponibilidade) {
    const anterior = valor;
    setValor(novo);
    setSalvando(true);
    try {
      await api('/workspace/agents/me/availability', { method: 'PATCH', body: { availability: novo } });
      toast(`Você está ${OPCOES.find((o) => o.valor === novo)!.rotulo.toLowerCase()}.`);
    } catch (erro) {
      // Reverte no erro. Sem isso o controle mostraria "Ocupado" enquanto o
      // servidor continua mandando conversas — o pior tipo de mentira numa
      // interface de operação.
      setValor(anterior);
      toast(erro instanceof ApiError ? erro.message : 'Não foi possível alterar sua disponibilidade.', 'danger');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card title="Minha disponibilidade" as="h2">
      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <Segmented
          rotuloGrupo="Minha disponibilidade"
          opcoes={OPCOES}
          valor={valor}
          onChange={(v) => void alterar(v)}
          disabled={salvando}
        />
        {/* A consequência escrita por extenso. "Ocupado" não diz o que muda —
            e o que muda é justamente se a fila continua chegando. */}
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {EFEITO[valor]}
        </p>
      </div>
    </Card>
  );
}

export function Operadores() {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-6)' }}>
      <MinhaDisponibilidade />
      <CrudScreen config={OPERADORES} />
    </div>
  );
}
